<template>
    <div
        v-if="notification"
        class="tw-bg-amber-500 tw-text-white tw-font-normal tw-font-dm-sans tw-text-base tw-absolute tw-top-0 tw-w-full tw-z-10 tw-p-3 tw-text-center"
    >
        {{ notification }}
    </div>
    <XeroTenantSelector v-else-if="currentRoute === 'integrations.xero.callback'" />
    <Error v-else-if="errorCode" :code="errorCode" :message="errorMessage" :reference="errorReference" />
    <GlobalComponents />
</template>
<script setup>
import { useDataStore } from '@/js/stores/DataStore.js';
import GlobalComponents from '@/js/components/Globals.vue';
import Error from '@/js/components/Error.vue';
import { isInertiaPage, toast, route } from '@/js/utils.js';
import { capturePosthogEvent, usePosthog } from '@/js/composables/usePosthog.js';
import XeroTenantSelector from '@/assets/js/eventbooks/components/setup/xero-tenant-selector.vue';
import { onMounted, onUnmounted, ref } from 'vue';
import { router } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { Eventbus } from '@/js/utils/eventbus.ts';
import { getLedger } from '@/js/composables/useLedger.js';

const posthog = usePosthog().posthog;
const dataStore = useDataStore();
const { current_user: currentUser } = storeToRefs(dataStore);
const errorCode = window.errorCode;
const errorMessage = window.errorMessage;
const errorReference = window.errorReference;
const notification = window.notif;
const currentRoute = ref(route().current());
const usesInertia = isInertiaPage();
let removeInertiaNavigateListener = () => {};
let lastViewedLocation = window.location.pathname + window.location.search;
const trackPageView = () => {
    const location = window.location.pathname + window.location.search;
    if (location === lastViewedLocation) {
        return;
    }
    lastViewedLocation = location;
    capturePosthogEvent('page_viewed', { route: route().current() });
};
const handleHistoryNavigation = () => {
    Eventbus.$emit('pageChanged');
    Eventbus.$emit('routeChanged');
};
const handleRouteChanged = () => {
    currentRoute.value = route().current();
    trackPageView();
};

onMounted(async () => {
    if (usesInertia) {
        removeInertiaNavigateListener = router.on('navigate', trackPageView);
    }
    await dataStore.initialise();
    if (window.location.pathname + window.location.search === lastViewedLocation) {
        capturePosthogEvent('page_viewed', { route: route().current() });
    }

    const currentEventId = dataStore.get('current_event.id', null);
    if (currentEventId) {
        await getLedger(currentEventId);
    }

    if (!usesInertia) {
        window.addEventListener('popstate', handleHistoryNavigation);
    }
    Eventbus.$on('routeChanged', handleRouteChanged);
    if (route().params.action) {
        setTimeout(() => handleRouteAction(JSON.parse(route().params.action)), 250);
    }

    if (posthog.value && currentUser) {
        posthog.value.identify(currentUser.id, {
            email: currentUser.email,
            name: currentUser.name,
        });
    }
});
onUnmounted(() => {
    window.removeEventListener('popstate', handleHistoryNavigation);
    Eventbus.$off('routeChanged', handleRouteChanged);
    removeInertiaNavigateListener();
});
const handleRouteAction = (action) => {
    if (!action.e) {
        toast('Error', 'No emitter defined in route action', 'error');
        return;
    }
    const args = action.a ?? [];
    Eventbus.$emit(action.e, ...args);
    const url = new URL(window.location.href);
    url.searchParams.delete('action');
    window.history.replaceState({}, '', url);
};
</script>
<style lang="scss">
.spa-page-wrapper {
    padding: 60px 0 0 0;
}
</style>
